import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { Paper, Typography, Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import monthName from '../../functions/month';
import { formatEuro } from '../../functions/formatNumber';

const assetColors = [
  '#4F46E5', // Indigo
  '#06B6D4', // Cyan
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#EC4899', // Pink
  '#8B5CF6', // Violet
  '#EF4444', // Red
  '#3B82F6', // Blue
  '#14B8A6', // Teal
  '#84CC16', // Lime
];

const shortMonthNames = [
  'Gen', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Des'
];

const formatDateTooltip = (dateKey) => {
  if (!dateKey) return '';
  const datePart = dateKey.split('T')[0];
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const [y, m, d] = parts;
    return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
  }
  return dateKey;
};

const HistoricInversioChart = ({ assets, isDaily = false, isWeekly = false, selectedAssetId = null }) => {
  const [chartMode, setChartMode] = useState('total'); // 'total' | 'actius'
  const [chartMetric, setChartMetric] = useState('guanyPerdua'); // 'guanyPerdua' | 'valor' | 'preuUnitari'

  if (!assets || assets.length === 0) {
    return (
      <Paper sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography variant="body1">No hi ha dades disponibles per al gràfic</Typography>
      </Paper>
    );
  }

  // 1. Cercar totes les dates úniques (keys) en format YYYY-MM-DD
  const uniqueDatesMap = {};
  assets.forEach((asset) => {
    if (asset.historicPreus) {
      asset.historicPreus.forEach((pt) => {
        if (pt.fecha) {
          const dateKey = pt.fecha.split('T')[0];
          uniqueDatesMap[dateKey] = new Date(pt.fecha);
        }
      });
    }
  });

  const sortedDateKeys = Object.keys(uniqueDatesMap).sort();

  if (sortedDateKeys.length === 0) {
    return (
      <Paper sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography variant="body1">No hi ha dades de l'històric per mostrar</Typography>
      </Paper>
    );
  }

  // Detecció automàtica de resolució setmanal per a l'anual si no s'ha especificat explícitament
  const effectiveIsWeekly = isWeekly || (!isDaily && sortedDateKeys.length > 20);

  // 2. Formatar etiquetes de l'eix X
  const labels = sortedDateKeys.map((key) => {
    const d = uniqueDatesMap[key];
    if (isDaily) {
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
    } else if (effectiveIsWeekly) {
      return formatDateTooltip(key);
    } else {
      const mName = monthName(d.getMonth() + 1);
      const year = d.getFullYear();
      return `${mName} ${year}`;
    }
  });

  // Helper per extreure el valor d'un punt segons la mètrica triada
  const getPointValue = (pt) => {
    if (!pt) return null;
    if (chartMetric === 'guanyPerdua') {
      if (pt.guanyPerdua !== undefined && pt.guanyPerdua !== null) {
        return Number(pt.guanyPerdua);
      }
      if (pt.valorTotal !== undefined && pt.valorInvertit !== undefined) {
        return Number(pt.valorTotal) - Number(pt.valorInvertit);
      }
      return null;
    }
    if (chartMetric === 'preuUnitari') {
      if (pt.preuUnitari !== undefined && pt.preuUnitari !== null && Number(pt.preuUnitari) > 0) {
        return Number(pt.preuUnitari);
      }
      return null;
    }
    if (pt.valorTotal !== undefined && pt.valorTotal !== null) {
      return Number(pt.valorTotal);
    }
    return null;
  };

  // 3. Crear dataset de Total
  const totalData = sortedDateKeys.map((key) => {
    let sum = 0;
    let hasPoints = false;
    assets.forEach((asset) => {
      if (asset.historicPreus) {
        const pt = asset.historicPreus.find((p) => p.fecha && (p.fecha.startsWith(key) || p.fecha.split('T')[0] === key));
        if (pt) {
          const val = getPointValue(pt);
          if (val !== null && !isNaN(val)) {
            sum += val;
            hasPoints = true;
          }
        }
      }
    });
    return hasPoints ? sum : null;
  });

  // 4. Crear datasets per actiu
  const assetDatasets = assets.map((asset, index) => {
    const color = assetColors[index % assetColors.length];
    const data = sortedDateKeys.map((key) => {
      if (asset.historicPreus) {
        const pt = asset.historicPreus.find((p) => p.fecha && (p.fecha.startsWith(key) || p.fecha.split('T')[0] === key));
        if (pt) {
          return getPointValue(pt);
        }
      }
      return null;
    });

    return {
      label: asset.nom,
      data: data,
      borderColor: color,
      backgroundColor: `${color}1A`,
      borderWidth: 2.5,
      pointRadius: isDaily || effectiveIsWeekly ? 0 : 3,
      pointHoverRadius: 6,
      pointHitRadius: 10,
      fill: false,
      tension: 0.2,
      spanGaps: true,
      assetId: asset.actiuId,
    };
  });

  // 5. Filtrar datasets segons mode de gràfic i actiu seleccionat
  let datasetsToShow = [];

  if (selectedAssetId !== null) {
    // Si hi ha un actiu seleccionat via clic a la taula, només mostrem aquest actiu
    const singleDataset = assetDatasets.find((ds) => ds.assetId === selectedAssetId);
    if (singleDataset) {
      datasetsToShow = [{
        ...singleDataset,
        borderWidth: 3,
        pointRadius: isDaily || effectiveIsWeekly ? 0 : 4,
        pointHoverRadius: 6,
        pointHitRadius: 10,
        fill: chartMetric !== 'preuUnitari',
        spanGaps: true,
      }];
    }
  } else {
    // Si no hi ha actiu seleccionat, depèn del botó (en mode preuUnitari es mostren sempre els actius individualment)
    const totalDataset = {
      label: 'Total',
      data: totalData,
      borderColor: '#3F51B5', // Blau corporatiu
      backgroundColor: 'rgba(63, 81, 181, 0.08)',
      borderWidth: 3.5,
      pointRadius: isDaily || effectiveIsWeekly ? 0 : 4,
      pointHoverRadius: 6,
      pointHitRadius: 10,
      fill: chartMode === 'total', // Només omplim l'àrea si és exclusiu total
      tension: 0.2,
      spanGaps: true,
      assetId: 'total',
    };

    if (chartMode === 'total' && chartMetric !== 'preuUnitari') {
      datasetsToShow = [totalDataset];
    } else {
      datasetsToShow = assetDatasets;
    }
  }

  const chartData = {
    labels: labels,
    datasets: datasetsToShow,
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: selectedAssetId === null, // Ocultar llegenda si només hi ha un actiu filtrat
        position: 'top',
        labels: {
          usePointStyle: true,
          boxWidth: 8,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        filter: function (tooltipItem) {
          return tooltipItem.parsed.y !== null && tooltipItem.parsed.y !== undefined && !isNaN(tooltipItem.parsed.y);
        },
        callbacks: {
          title: function (tooltipItems) {
            if (!tooltipItems || tooltipItems.length === 0) return '';
            const idx = tooltipItems[0].dataIndex;
            const key = sortedDateKeys[idx];
            if ((effectiveIsWeekly || isDaily) && key) {
              return formatDateTooltip(key);
            }
            return tooltipItems[0].label || '';
          },
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null && context.parsed.y !== undefined) {
              const val = context.parsed.y;
              if (chartMetric === 'guanyPerdua') {
                label += `${val >= 0 ? '+' : ''}${formatEuro(val)}`;
              } else {
                label += formatEuro(val);
              }
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: !effectiveIsWeekly,
          maxRotation: 0,
          minRotation: 0,
          maxTicksLimit: effectiveIsWeekly ? 54 : (isDaily ? 10 : 12),
          callback: function (val, index) {
            if (effectiveIsWeekly) {
              const idx = typeof val === 'number' ? val : index;
              const key = sortedDateKeys[idx];
              if (!key) return '';
              const parts = key.split('T')[0].split('-');
              const monthIdx = parseInt(parts[1], 10) - 1;

              const prevKey = idx > 0 ? sortedDateKeys[idx - 1] : null;
              const prevParts = prevKey ? prevKey.split('T')[0].split('-') : null;
              const prevMonthIdx = prevParts ? parseInt(prevParts[1], 10) - 1 : null;

              // Només renderitzar quan hi ha canvi de mes o és el primer punt
              if (prevMonthIdx === null || prevMonthIdx !== monthIdx) {
                return shortMonthNames[monthIdx] || '';
              }
              return '';
            }
            return this.getLabelForValue(val);
          },
        },
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          callback: function (value) {
            return formatEuro(value);
          },
        },
        grace: chartMetric === 'preuUnitari' ? '5%' : 0,
      },
    },
  };

  const getMetricTitleLabel = () => {
    if (chartMetric === 'guanyPerdua') return 'Guany / Pèrdua';
    if (chartMetric === 'preuUnitari') return 'Preu Unitari';
    return 'Valor de la Cartera';
  };

  const getMetricDetailLabel = () => {
    if (chartMetric === 'guanyPerdua') return 'Guany/Pèrdua';
    if (chartMetric === 'preuUnitari') return 'Preu Unitari';
    return 'Valor';
  };

  return (
    <Paper sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
        <Typography variant="h6">
          {selectedAssetId !== null 
            ? `Detall: ${assets.find(a => a.actiuId === selectedAssetId)?.nom || ''} (${getMetricDetailLabel()})`
            : `Evolució Inversió: ${getMetricTitleLabel()}`}
        </Typography>

        <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Mètrica: Guany/Pèrdua vs Valor vs Preu Unitari */}
          <ToggleButtonGroup
            value={chartMetric}
            exclusive
            onChange={(e, val) => {
              if (val) {
                setChartMetric(val);
                if (val === 'preuUnitari' && chartMode === 'total') {
                  setChartMode('actius');
                }
              }
            }}
            size="small"
            color="primary"
          >
            <ToggleButton value="guanyPerdua">Guany / Pèrdua</ToggleButton>
            <ToggleButton value="valor">Valor Actiu</ToggleButton>
            <ToggleButton value="preuUnitari">Preu Unitari</ToggleButton>
          </ToggleButtonGroup>

          {/* Mode de visualització */}
          {selectedAssetId === null && (
            <ToggleButtonGroup
              value={chartMetric === 'preuUnitari' ? 'actius' : chartMode}
              exclusive
              onChange={(e, val) => val && setChartMode(val)}
              size="small"
            >
              <ToggleButton value="total" disabled={chartMetric === 'preuUnitari'}>Total</ToggleButton>
              <ToggleButton value="actius">Actius</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {selectedAssetId !== null && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', width: '100%', mt: -1 }}>
            Filtre actiu de taula (Fes clic de nou en la fila per restaurar)
          </Typography>
        )}
      </Box>

      <Box sx={{ height: '350px', position: 'relative' }}>
        <Line data={chartData} options={chartOptions} />
      </Box>
    </Paper>
  );
};

export default HistoricInversioChart;
